import { formatDate, now } from "common";
import { Dayjs } from "dayjs";
import { memoize } from "lodash";
import { CutoffType, Timeslot } from "types";
import {
    getAvailableEquipmentUnits,
    getIsEventOverlap,
    isEquipmentTimeslot,
    isTimeslotInProgress,
} from "../../models/Timeslot";
import { BookableDates } from "./ArrivalDatePicker";
import { convertMinutesToMs } from "@/helpers/durationHelper";

export interface TimeslotFilterOptions {
    cutoffDuration?: number | null;
    cutoffType: CutoffType | null;
}

interface GetBookableDatesProps {
    timeslots: Timeslot[];
    product: ID;
    allowPastDates: boolean;
    showAllTimeslots: boolean;
    allowOverlap: boolean;
    isMist: boolean;
    initialArrival?: Dayjs;
}

export const getBookableDates = ({
    timeslots,
    product,
    allowPastDates,
    showAllTimeslots,
    allowOverlap,
    isMist,
    initialArrival,
}: GetBookableDatesProps) => {
    const bookableDates: BookableDates = {};
    const pastDates: BookableDates = {};
    const nowString = formatDate(new Date());
    const memoizedOverlap = memoize(getIsEventOverlap);

    for (const timeslot of timeslots) {
        const isPast = timeslot.date < nowString && !isTimeslotInProgress(timeslot);
        const isEventOverlap = initialArrival && allowOverlap && memoizedOverlap(timeslot, initialArrival);
        const canShowTimeSlot =
            (!(!showAllTimeslots && timeslot.capacity.open === 0) || allowOverlap) &&
            (timeslot.canBook ||
                (timeslot.canWaitlist && !isMist && !isPast) ||
                (timeslot.capacity.open > 0 && !isPast) ||
                (showAllTimeslots && !isPast) ||
                (isEventOverlap && !isPast) ||
                (timeslot.capacity.open > 0 && allowPastDates) ||
                (showAllTimeslots && allowPastDates));

        if (timeslot.product !== product) {
            continue;
        }

        if (canShowTimeSlot) {
            if (!bookableDates[timeslot.date]) {
                bookableDates[timeslot.date] = [];
            }

            const existingTimeslotIndex = bookableDates[timeslot.date].findIndex((timeslotItem) => {
                return timeslotItem.time === timeslot.time;
            });

            if (existingTimeslotIndex === -1) {
                bookableDates[timeslot.date].push(timeslot);
            } else if (!bookableDates[timeslot.date][existingTimeslotIndex].canBook && timeslot.canBook) {
                // for multi-private events ensure a bookable timeslot is shown.
                bookableDates[timeslot.date][existingTimeslotIndex] = timeslot;
            }
        } else if (isPast && timeslot.canBook) {
            if (!pastDates[timeslot.date]) {
                pastDates[timeslot.date] = [];
            }

            pastDates[timeslot.date].push(timeslot);
        }
    }

    return [bookableDates, pastDates];
};

export const getWaitlistDates = (timeslots: Timeslot[], bookableDates: BookableDates): Dayjs[] => {
    return timeslots
        .filter((timeslot) => {
            if (!timeslot.canWaitlist) {
                return false;
            }

            const formattedWaitlistDate = formatDate(timeslot.arrivalDatetime);
            const bookableTimeslotsForWaitlistDate = bookableDates[formattedWaitlistDate];
            if (bookableTimeslotsForWaitlistDate) {
                for (const bookableSlot of bookableTimeslotsForWaitlistDate) {
                    if (bookableSlot.time === timeslot.time && bookableSlot.canBook) {
                        return false;
                    }
                }
            }

            return true;
        })
        .map((timeslot) => timeslot.arrivalDatetime);
};

export const getBookableSlots = (arrivalDate: string, bookableDates: BookableDates): Timeslot[] => {
    const dates = Object.keys(bookableDates);
    const timeslots = dates.length > 0 ? bookableDates[arrivalDate] ?? bookableDates[dates[0]] : [];

    return timeslots.filter((timeslot) => timeslot.canBook || timeslot.canWaitlist);
};

export const getNextBookableDate = (arrivalDate: string, bookableDates: BookableDates) => {
    const dates = Object.keys(bookableDates);
    const arrivalDayStartTs = now(arrivalDate).startOf("day").valueOf();
    const nextDate = dates.find((date) => {
        const hasBookableDates = bookableDates[date].length > 0;
        const canBook = bookableDates[date].find((timeslot: Timeslot) => timeslot.canBook || timeslot.canWaitlist);
        const ifAfterSelectedDate = now(date).valueOf() > arrivalDayStartTs;
        return hasBookableDates && canBook && ifAfterSelectedDate;
    });

    if (nextDate) {
        const timeslot = bookableDates[nextDate][0];
        const time = timeslot?.time ?? null;
        return { date: nextDate, time, timeslot };
    }

    return null;
};

export const getFirstAvailableTimeslot = (
    bookableTimeslots: Timeslot[],
    isPastAvailabilityChecked: boolean,
    selectedDate: string | null = null,
    filterOptions?: TimeslotFilterOptions,
    bookableOnly = false,
): Timeslot | undefined => {
    if (isPastAvailabilityChecked && bookableTimeslots.length > 0) {
        return bookableTimeslots[0];
    }

    const nowDate = now().valueOf();
    const selectedStartTs = selectedDate ? now(selectedDate).startOf("day").valueOf() : null;

    for (const slot of bookableTimeslots) {
        if (bookableOnly && !slot.canBook) {
            continue;
        }

        const slotTs = slot.arrivalDatetime.valueOf();
        if (selectedStartTs !== null && slotTs < selectedStartTs) {
            continue;
        }

        const slotTsWithCutoff = filterOptions ? getSlotTsWithCutoff(slot, filterOptions) : slotTs;
        const effectiveSlotTs = slotTsWithCutoff > slotTs ? slotTsWithCutoff : slotTs;

        if (slot.time && effectiveSlotTs <= nowDate) {
            continue;
        }

        return slot;
    }

    return undefined;
};

const getSlotTsWithCutoff = (slot: Timeslot, filterOptions: TimeslotFilterOptions) => {
    const cutoffDurationMs = filterOptions.cutoffDuration ? convertMinutesToMs(filterOptions.cutoffDuration) : 0;
    /**
     * Three possible scenarios:
     * cutoffType is PRE_END: The cutoff duration is subtracted from the end time of the slot.
     * cutoffType is PRE_START: The cutoff duration is subtracted from the arrival time of the slot.
     * No cutoff: The arrival time of the slot is used `slotTs` (cutoffDurationMs will always be 0).
     */
    const referenceDateTime = filterOptions.cutoffType === CutoffType.PRE_END ? slot.end : slot.arrivalDatetime;

    return referenceDateTime.valueOf() - cutoffDurationMs;
};

export const getCurrentMonthRange = (date: string) => {
    const startDate = now(date);
    const start = formatDate(startDate.startOf("day"));
    const end = formatDate(startDate.endOf("month"));
    return { start, end };
};

export const getMonthRange = (date: string, addMonth: number) => {
    const startDate = now(date);
    const nextMonth = startDate.add(addMonth, "month");
    const start = formatDate(nextMonth.startOf("month"));
    const end = formatDate(nextMonth.endOf("month"));
    return { start, end };
};

export const getFirstBookableTimeslot = (
    bookableTimeslots: Timeslot[],
    isPastAvailabilityChecked: boolean,
    selectedDate: string | null = null,
    filterOptions?: TimeslotFilterOptions,
): Timeslot | undefined => {
    return getFirstAvailableTimeslot(bookableTimeslots, isPastAvailabilityChecked, selectedDate, filterOptions, true);
};

export const isSlotUnavailable = (date: Dayjs | Date, bookableDates: BookableDates) => {
    const dateString = formatDate(date);
    const slots = bookableDates[dateString];
    const isTodayOrLater = dateString >= formatDate(now());
    if (!slots) return isTodayOrLater;
    // Equipment books whole units, so availability is the unit count, not open seats
    // (`open` is count * capacity, which overstates what can be booked).
    const zeroAvailability = slots.every((slot) =>
        isEquipmentTimeslot(slot) ? getAvailableEquipmentUnits(slot) <= 0 : slot.capacity.open <= 0,
    );
    return isTodayOrLater && zeroAvailability;
};

export type UnavailabilityMessagesByDate = Record<string, string[]>;

export const getUnavailabilityMessagesByDate = (timeslots: Timeslot[], product: ID): UnavailabilityMessagesByDate => {
    const messagesByDate: UnavailabilityMessagesByDate = {};
    for (const timeslot of timeslots) {
        const message = timeslot.unavailability?.message;
        if (timeslot.product !== product || !message) {
            continue;
        }

        const messages = messagesByDate[timeslot.date] ?? [];
        if (!messages.includes(message)) {
            messagesByDate[timeslot.date] = [...messages, message];
        }
    }

    return messagesByDate;
};
